// ===========================================
// Notifications Card Component
// ===========================================
// Displays dismissible notifications for parents with navigation

import { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Skeleton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Close as CloseIcon,
  Description as NoteIcon,
  Receipt as InvoiceIcon,
  EventAvailable as BookingIcon,
  InsertDriveFile as FileIcon,
  Person as PersonIcon,
  School as SchoolIcon,
  Visibility as VisibilityIcon,
  Payment as PaymentIcon,
  Cancel as CancelIcon,
  EditCalendar as RescheduleIcon,
  NotificationsActive as AbsenceIcon,
  CardGiftcard as MakeupCreditIcon,
  Celebration as EventIcon,
  FactCheck as FactCheckIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { ParentNotification, ParentNotificationType } from '../../api/dashboard.api';

// ===========================================
// TYPES
// ===========================================

interface NotificationsCardProps {
  notifications: ParentNotification[];
  isLoading: boolean;
  onDismiss: (type: string, targetId: string) => void;
  onNavigate: (notification: ParentNotification) => void;
}

// ===========================================
// HELPER FUNCTIONS
// ===========================================

function getNotificationIcon(type: ParentNotificationType) {
  switch (type) {
    case 'LESSON_NOTE':
      return <NoteIcon />;
    case 'INVOICE':
      return <InvoiceIcon />;
    case 'PAYMENT':
      return <PaymentIcon />;
    case 'HYBRID_BOOKING':
      return <BookingIcon />;
    case 'HYBRID_BOOKING_OPEN':
      return <BookingIcon />;
    case 'HYBRID_BOOKING_RESCHEDULED':
      return <RescheduleIcon />;
    case 'HYBRID_BOOKING_CANCELLED':
      return <CancelIcon />;
    case 'FILE_UPLOAD':
      return <FileIcon />;
    case 'LESSON_CANCELLED':
      return <CancelIcon />;
    case 'ABSENCE_REPORTED':
      return <AbsenceIcon />;
    case 'ABSENCE_CANCELLED':
      return <CancelIcon />;
    case 'MAKEUP_CREDIT':
      return <MakeupCreditIcon />;
    case 'EVENT_PUBLISHED':
      return <EventIcon />;
    case 'PERMISSIONSLIP':
      return <FactCheckIcon />;
    default:
      return <NotificationsIcon />;
  }
}

function getNotificationColor(type: ParentNotificationType): 'primary' | 'error' | 'success' | 'info' | 'warning' {
  // Colors match the stat tiles on the parent dashboard
  switch (type) {
    case 'LESSON_NOTE':
      return 'success'; // Green - matches "Notifications" stat tile
    case 'INVOICE':
      return 'error'; // Red - matches "Outstanding" stat tile (overdue)
    case 'PAYMENT':
      return 'success'; // Green - payment received is positive
    case 'HYBRID_BOOKING':
      return 'info'; // Blue - matches "Bookings" stat tile
    case 'HYBRID_BOOKING_OPEN':
      return 'warning'; // Orange - action required
    case 'HYBRID_BOOKING_RESCHEDULED':
      return 'warning'; // Orange - schedule change to be aware of
    case 'HYBRID_BOOKING_CANCELLED':
      return 'error'; // Red - cancellation is important
    case 'FILE_UPLOAD':
      return 'primary'; // Primary blue - for files/resources
    case 'LESSON_CANCELLED':
      return 'error'; // Red - cancellation is important
    case 'ABSENCE_REPORTED':
      return 'warning'; // Orange - informational action taken
    case 'ABSENCE_CANCELLED':
      return 'error'; // Red - cancellation
    case 'MAKEUP_CREDIT':
      return 'info';
    case 'EVENT_PUBLISHED':
      return 'info'; // Blue - informational, a new event to know about
    case 'PERMISSIONSLIP':
      return 'warning'; // Orange - action required, and usually deadline-bound
    default:
      return 'primary';
  }
}

function getTypeLabel(type: ParentNotificationType): string {
  switch (type) {
    case 'LESSON_NOTE':
      return 'Note';
    case 'INVOICE':
      return 'Invoice';
    case 'PAYMENT':
      return 'Payment';
    case 'HYBRID_BOOKING':
      return 'Booking';
    case 'HYBRID_BOOKING_OPEN':
      return 'Action';
    case 'HYBRID_BOOKING_RESCHEDULED':
      return 'Rescheduled';
    case 'HYBRID_BOOKING_CANCELLED':
      return 'Cancelled';
    case 'FILE_UPLOAD':
      return 'File';
    case 'LESSON_CANCELLED':
      return 'Cancelled';
    case 'ABSENCE_REPORTED':
      return 'Absence';
    case 'ABSENCE_CANCELLED':
      return 'Cancelled';
    case 'MAKEUP_CREDIT':
      return 'Makeup';
    case 'EVENT_PUBLISHED':
      return 'Event';
    case 'PERMISSIONSLIP':
      return 'Sign';
    default:
      return '';
  }
}

// ===========================================
// COMPONENT
// ===========================================

export default function NotificationsCard({
  notifications,
  isLoading,
  onDismiss,
  onNavigate,
}: NotificationsCardProps) {
  // State for note modal
  const [noteModalOpen, setNoteModalOpen] = useState(false);
  const [selectedNote, setSelectedNote] = useState<ParentNotification | null>(null);

  // Handle click on notification item
  const handleClick = (notification: ParentNotification) => {
    // For LESSON_NOTE and LESSON_CANCELLED, show modal instead of navigating
    if (notification.type === 'LESSON_NOTE' || notification.type === 'LESSON_CANCELLED') {
      setSelectedNote(notification);
      setNoteModalOpen(true);
      return;
    }

    // Makeup credit is a synthetic local item — just navigate, no server dismiss
    if (notification.type === 'MAKEUP_CREDIT') {
      onNavigate(notification);
      return;
    }

    // For other types, navigate and dismiss
    onNavigate(notification);
    const [type, targetId] = extractTypeAndTargetId(notification.id);
    onDismiss(type, targetId);
  };

  // Handle closing the note modal
  const handleCloseNoteModal = () => {
    setNoteModalOpen(false);
    setSelectedNote(null);
  };

  // Handle dismissing note from modal
  const handleDismissFromModal = () => {
    if (selectedNote) {
      const [type, targetId] = extractTypeAndTargetId(selectedNote.id);
      onDismiss(type, targetId);
    }
    handleCloseNoteModal();
  };

  // Handle dismiss button click
  const handleDismiss = (e: React.MouseEvent, notification: ParentNotification) => {
    e.stopPropagation(); // Prevent triggering the item click
    const [type, targetId] = extractTypeAndTargetId(notification.id);
    onDismiss(type, targetId);
  };

  // Extract type and targetId from notification id (format: TYPE_targetId)
  const extractTypeAndTargetId = (id: string): [string, string] => {
    // ID format: LESSON_NOTE_uuid or INVOICE_uuid, etc.
    const parts = id.split('_');
    if (parts.length >= 3 && parts[0] === 'LESSON' && parts[1] === 'NOTE') {
      // LESSON_NOTE_uuid
      return ['LESSON_NOTE', parts.slice(2).join('_')];
    } else if (parts.length >= 3 && parts[0] === 'LESSON' && parts[1] === 'CANCELLED') {
      // LESSON_CANCELLED_uuid
      return ['LESSON_CANCELLED', parts.slice(2).join('_')];
    } else if (parts.length >= 3 && parts[0] === 'FILE') {
      // FILE_UPLOAD_uuid
      return ['FILE_UPLOAD', parts.slice(2).join('_')];
    } else if (parts.length >= 3 && parts[0] === 'HYBRID') {
      // HYBRID_BOOKING_uuid or HYBRID_BOOKING_OPEN_uuid
      if (parts[1] === 'BOOKING' && parts[2] === 'OPEN') {
        return ['HYBRID_BOOKING_OPEN', parts.slice(3).join('_')];
      }
      return ['HYBRID_BOOKING', parts.slice(2).join('_')];
    } else if (parts.length >= 3 && parts[0] === 'ABSENCE' && parts[1] === 'REPORTED') {
      // ABSENCE_REPORTED_uuid
      return ['ABSENCE_REPORTED', parts.slice(2).join('_')];
    } else if (parts.length >= 3 && parts[0] === 'ABSENCE' && parts[1] === 'CANCELLED') {
      // ABSENCE_CANCELLED_uuid
      return ['ABSENCE_CANCELLED', parts.slice(2).join('_')];
    } else if (parts.length >= 2) {
      // INVOICE_uuid, PAYMENT_uuid
      return [parts[0], parts.slice(1).join('_')];
    }
    return [id, ''];
  };

  // Loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader title="Notifications" />
        <CardContent>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={50} sx={{ mb: 1 }} />
          ))}
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (notifications.length === 0) {
    return (
      <Card>
        <CardHeader
          title="Notifications"
          avatar={<NotificationsIcon color="action" />}
        />
        <CardContent>
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
            <Typography color="text.secondary">
              No new notifications
            </Typography>
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader
          title="Notifications"
          subheader={`${notifications.length} new`}
          avatar={<NotificationsIcon color="primary" />}
        />
        <CardContent sx={{ p: 0 }}>
          <List disablePadding sx={{ maxHeight: 504, overflowY: 'auto' }}>
            {notifications.map((notification, index) => {
              const color = getNotificationColor(notification.type);
              const timeAgo = formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true });

              return (
                <ListItem
                  key={notification.id}
                  divider={index < notifications.length - 1}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      bgcolor: 'action.hover',
                    },
                    pr: 1,
                  }}
                  onClick={() => handleClick(notification)}
                  secondaryAction={
                    notification.type !== 'MAKEUP_CREDIT' ? (
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => handleDismiss(e, notification)}
                        sx={{
                          color: 'text.secondary',
                          '&:hover': { color: 'error.main' },
                        }}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    ) : undefined
                  }
                >
                  <ListItemIcon sx={{ minWidth: 40, color: `${color}.main` }}>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="medium" noWrap>
                          {notification.title}
                        </Typography>
                        {/* For LESSON_NOTE, show instrument with its color; otherwise show type label */}
                        {notification.type === 'LESSON_NOTE' && notification.instrumentName ? (
                          <Chip
                            label={notification.instrumentName}
                            size="small"
                            sx={{
                              height: 18,
                              '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' },
                              bgcolor: notification.instrumentColor || 'primary.main',
                              color: 'white',
                            }}
                          />
                        ) : (
                          <Chip
                            label={getTypeLabel(notification.type)}
                            size="small"
                            color={color}
                            sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }}
                          />
                        )}
                        {/* Visibility marker for LESSON_NOTE - same colors as teacher notes */}
                        {notification.type === 'LESSON_NOTE' && notification.noteVisibility && (
                          <Chip
                            icon={<VisibilityIcon sx={{ fontSize: 12 }} />}
                            label={notification.noteVisibility === 'STUDENTS' ? 'Student' : 'Parent'}
                            size="small"
                            color={notification.noteVisibility === 'STUDENTS' ? 'success' : 'warning'}
                            sx={{
                              height: 18,
                              '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' },
                              '& .MuiChip-icon': { ml: 0.5 },
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          noWrap
                          sx={{ maxWidth: 200 }}
                        >
                          {notification.message}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.25 }}>
                          <Typography variant="caption" color="text.disabled">
                            {timeAgo}
                          </Typography>
                          {notification.studentName && (
                            <Typography variant="caption" color="primary.main">
                              {notification.studentName.split(' ')[0]}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        </CardContent>
      </Card>

      {/* Note Modal */}
      <Dialog
        open={noteModalOpen}
        onClose={handleCloseNoteModal}
        maxWidth="sm"
        fullWidth
      >
        {selectedNote && (
          <>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {selectedNote.type === 'LESSON_CANCELLED' ? (
                <>
                  <CancelIcon color="error" />
                  Class Cancelled
                </>
              ) : (
                <>
                  <NoteIcon color="primary" />
                  Class Note
                </>
              )}
            </DialogTitle>
            <DialogContent>
              {/* Class & Student Info */}
              <Box sx={{ mb: 2 }}>
                {selectedNote.lessonName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <SchoolIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      {selectedNote.lessonName}
                    </Typography>
                    {selectedNote.instrumentName && (
                      <Chip
                        label={selectedNote.instrumentName}
                        size="small"
                        sx={{
                          height: 20,
                          bgcolor: selectedNote.instrumentColor || 'primary.main',
                          color: 'white',
                        }}
                      />
                    )}
                    {selectedNote.noteVisibility && (
                      <Chip
                        icon={<VisibilityIcon sx={{ fontSize: 14 }} />}
                        label={selectedNote.noteVisibility === 'STUDENTS' ? 'Student' : 'Parent'}
                        size="small"
                        color={selectedNote.noteVisibility === 'STUDENTS' ? 'success' : 'warning'}
                        sx={{
                          height: 20,
                          '& .MuiChip-icon': { ml: 0.5 },
                        }}
                      />
                    )}
                  </Box>
                )}
                {selectedNote.studentName && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      {selectedNote.studentName}
                    </Typography>
                  </Box>
                )}
                <Typography variant="caption" color="text.disabled">
                  {format(new Date(selectedNote.createdAt), 'd MMMM yyyy')}
                  {selectedNote.authorName && ` • By ${selectedNote.authorName}`}
                </Typography>
              </Box>

              <Divider sx={{ mb: 2 }} />

              {/* Note Content or Cancellation Reason */}
              {selectedNote.type === 'LESSON_CANCELLED' ? (
                <>
                  <Typography variant="subtitle2" color="error" sx={{ mb: 1 }}>
                    This class has been cancelled
                  </Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedNote.noteContent === 'No reason provided' ? (
                      <em style={{ color: '#9DA5AF' }}>No reason provided</em>
                    ) : (
                      selectedNote.noteContent || selectedNote.message
                    )}
                  </Typography>
                </>
              ) : (
                <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                  {selectedNote.noteContent || selectedNote.message}
                </Typography>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseNoteModal} color="inherit">
                Close
              </Button>
              <Button onClick={handleDismissFromModal} variant="contained" color="primary">
                Mark as Read
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
